import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChartBarIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';

const Reports = () => {
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().slice(0, 10));
  const [dailyStock, setDailyStock] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [dailyError, setDailyError] = useState('');
  const [damageFilters, setDamageFilters] = useState({
    startDate: new Date().toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10)
  });
  const [damagedRows, setDamagedRows] = useState([]);
  const [damagedTotals, setDamagedTotals] = useState({ totalDamagedQty: 0, totalDamagedValue: 0 });
  const [damagedLoading, setDamagedLoading] = useState(true);
  const [damagedError, setDamagedError] = useState('');

  useEffect(() => {
    fetchReports();
  }, [selectedMonth, dateRange, filterMode]);

  useEffect(() => {
    fetchDailyStock();
  }, [dailyDate]);

  useEffect(() => {
    fetchDamagedStock();
  }, [damageFilters]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = filterMode === 'month' 
        ? { month: selectedMonth }
        : { 
            startDate: dateRange.startDate || undefined, 
            endDate: dateRange.endDate || undefined 
          };
      
      const response = await axios.get('/api/reports/monthly', { params });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (event) => {
    const { name, value } = event.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const fetchDailyStock = async () => {
    setDailyLoading(true);
    setDailyError('');
    try {
      const response = await axios.get('/api/reports/daily-stock', {
        params: { date: dailyDate }
      });
      setDailyStock(response.data);
    } catch (error) {
      console.error('Error fetching daily stock report:', error);
      setDailyError('Failed to load daily stock report.');
    } finally {
      setDailyLoading(false);
    }
  };

  const fetchDamagedStock = async () => {
    setDamagedLoading(true);
    setDamagedError('');
    try {
      const response = await axios.get('/api/reports/damaged-stock', {
        params: {
          startDate: damageFilters.startDate || undefined,
          endDate: damageFilters.endDate || undefined
        }
      });
      setDamagedRows(response.data.rows || []);
      setDamagedTotals(response.data.totals || { totalDamagedQty: 0, totalDamagedValue: 0 });
    } catch (error) {
      console.error('Error fetching damaged stock report:', error);
      setDamagedError('Failed to load damaged stock.');
    } finally {
      setDamagedLoading(false);
    }
  };

  const handleDamageFilterChange = (event) => {
    const { name, value } = event.target;
    setDamageFilters((prev) => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const salesByProduct = reports?.salesByProduct || [];
  const expensesByCategory = reports?.expensesByCategory || [];
  const factoryCreditsTotal = reports?.factoryCreditsTotal || 0;
  
  // Use backend calculated totals if available, otherwise calculate on frontend
  const totalRevenue = reports?.totals?.totalRevenue || salesByProduct.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
  const totalCost = reports?.totals?.totalCost || 0;
  const totalExpenses = reports?.totals?.totalExpenses || expensesByCategory.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
  const grossProfit = reports?.totals?.grossProfit || (totalRevenue - totalCost);
  const netProfit = reports?.totals?.netProfit || (totalRevenue - totalCost - totalExpenses + factoryCreditsTotal);
  const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Reports</h1>
            <p className="mt-1 text-sm text-secondary-600">
              Generate and view business reports and analytics.
            </p>
          </div>
          <button className="btn-primary flex items-center gap-2">
            <DocumentArrowDownIcon className="h-5 w-5" />
            Export Report
          </button>
        </div>

        <div className="filter-section mt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">Filter By</label>
              <select
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value)}
                className="input-field"
              >
                <option value="month">Month</option>
                <option value="dateRange">Date Range</option>
              </select>
            </div>

            {filterMode === 'month' ? (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Select Month</label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="input-field"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={dateRange.startDate}
                    onChange={handleDateRangeChange}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={dateRange.endDate}
                    onChange={handleDateRangeChange}
                    className="input-field"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Period Display */}
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 border-2 border-primary-200">
        <p className="text-sm font-medium text-secondary-700">Viewing Report For:</p>
        <p className="text-lg font-bold text-primary-700">
          {filterMode === 'month' 
            ? new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
            : dateRange.startDate && dateRange.endDate 
              ? `${new Date(dateRange.startDate).toLocaleDateString()} - ${new Date(dateRange.endDate).toLocaleDateString()}`
              : 'Select date range'
          }
        </p>
      </div>

      {/* Report Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-100">
                <ChartBarIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Revenue</p>
                <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(totalRevenue)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                <ChartBarIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Cost of Goods</p>
                <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(totalCost)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                <ChartBarIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Expenses</p>
                <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(totalExpenses)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100">
                <ChartBarIcon className="h-6 w-6 text-accent-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Factory Credits</p>
                <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(factoryCreditsTotal)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${netProfit >= 0 ? 'bg-primary-100' : 'bg-red-100'}`}>
                <ChartBarIcon className={`h-6 w-6 ${netProfit >= 0 ? 'text-primary-600' : 'text-red-600'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Net Profit</p>
                <p className={`text-2xl font-semibold ${netProfit >= 0 ? 'text-success-600' : 'text-red-600'}`}>
                  {formatCurrency(netProfit)}
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  Margin: {profitMargin.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profit Breakdown */}
      <div className="card bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Profit Calculation Breakdown</h3>
        </div>
        <div className="card-body space-y-3">
          <div className="flex justify-between border-b border-secondary-200 pb-2">
            <span className="text-sm font-medium text-secondary-700">Total Revenue (Sales):</span>
            <span className="text-sm font-bold text-success-600">{formatCurrency(totalRevenue)}</span>
          </div>
          <div className="flex justify-between border-b border-secondary-200 pb-2">
            <span className="text-sm font-medium text-secondary-700">Cost of Goods Sold (COGS):</span>
            <span className="text-sm font-bold text-orange-600">-{formatCurrency(totalCost)}</span>
          </div>
          <div className="flex justify-between border-b border-secondary-200 pb-2">
            <span className="text-sm font-medium text-secondary-700">Gross Profit:</span>
            <span className="text-sm font-bold text-secondary-900">{formatCurrency(grossProfit)}</span>
          </div>
          <div className="flex justify-between border-b border-secondary-200 pb-2">
            <span className="text-sm font-medium text-secondary-700">Operating Expenses:</span>
            <span className="text-sm font-bold text-red-600">-{formatCurrency(totalExpenses)}</span>
          </div>
          <div className="flex justify-between border-b border-secondary-200 pb-2">
            <span className="text-sm font-medium text-secondary-700">Factory Credits:</span>
            <span className="text-sm font-bold text-accent-600">+{formatCurrency(factoryCreditsTotal)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t-2 border-secondary-300">
            <span className="text-base font-bold text-secondary-900">Net Profit:</span>
            <span className={`text-2xl font-bold ${netProfit >= 0 ? 'text-success-600' : 'text-red-600'}`}>
              {formatCurrency(netProfit)}
            </span>
          </div>
          <div className="text-center pt-2">
            <span className="text-sm text-secondary-600">Profit Margin: </span>
            <span className={`text-lg font-bold ${netProfit >= 0 ? 'text-success-600' : 'text-red-600'}`}>
              {profitMargin.toFixed(1)}%
            </span>
          </div>
          <div className="mt-2 text-xs text-secondary-500 bg-white rounded p-2">
            <p className="font-semibold mb-1">Formula:</p>
            <p>Net Profit = Revenue - Cost of Goods - Expenses + Factory Credits</p>
          </div>
        </div>
      </div>

      {/* Daily Stock Movement */}
      <div className="card bg-secondary-50">
        <div className="card-header">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-medium text-secondary-900">Daily Stock Movement</h3>
              <p className="text-sm text-secondary-600">
                Arrived, sold, damaged, and remaining stock for the day.
              </p>
            </div>
            <input
              type="date"
              value={dailyDate}
              onChange={(e) => setDailyDate(e.target.value)}
              className="input-field"
            />
          </div>
        </div>
        <div className="card-body space-y-4">
          {dailyLoading ? (
            <p className="text-sm text-secondary-500">Loading daily stock summary...</p>
          ) : dailyError ? (
            <p className="text-sm text-red-600">{dailyError}</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-lg border border-secondary-100 bg-white p-4">
                  <p className="text-xs uppercase text-secondary-500">Arrived</p>
                  <p className="text-xl font-semibold text-secondary-900">
                    {dailyStock?.totals?.arrived || 0}
                  </p>
                </div>
                <div className="rounded-lg border border-secondary-100 bg-white p-4">
                  <p className="text-xs uppercase text-secondary-500">Sold</p>
                  <p className="text-xl font-semibold text-secondary-900">
                    {dailyStock?.totals?.sold || 0}
                  </p>
                </div>
                <div className="rounded-lg border border-secondary-100 bg-white p-4">
                  <p className="text-xs uppercase text-secondary-500">Damaged</p>
                  <p className="text-xl font-semibold text-secondary-900">
                    {dailyStock?.totals?.damaged || 0}
                  </p>
                </div>
                <div className="rounded-lg border border-secondary-100 bg-white p-4">
                  <p className="text-xs uppercase text-secondary-500">Remaining</p>
                  <p className="text-xl font-semibold text-secondary-900">
                    {dailyStock?.totals?.remaining || 0}
                  </p>
                </div>
              </div>

              {dailyStock?.perProduct?.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200">
                    <thead className="bg-secondary-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Product</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Arrived</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Sold</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Damaged</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Remaining</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-secondary-100">
                      {dailyStock.perProduct.map((item, index) => (
                        <tr key={item.product?._id || index}>
                          <td className="px-4 py-2 text-sm text-secondary-900">
                            {item.product?.name || 'Unknown'}{item.product?.size ? ` (${item.product.size})` : ''}
                          </td>
                          <td className="px-4 py-2 text-sm text-secondary-900">{item.arrived}</td>
                          <td className="px-4 py-2 text-sm text-secondary-900">{item.sold}</td>
                          <td className="px-4 py-2 text-sm text-secondary-900">{item.damaged}</td>
                          <td className="px-4 py-2 text-sm text-secondary-900">{item.remaining}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-secondary-500">No stock activity for this day.</p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Damaged Stock */}
      <div className="card bg-secondary-50">
        <div className="card-header">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-medium text-secondary-900">Damaged Stock</h3>
              <p className="text-sm text-secondary-600">
                Damaged quantities from arrivals and approved returns.
              </p>
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <input
                type="date"
                name="startDate"
                value={damageFilters.startDate}
                onChange={handleDamageFilterChange}
                className="input-field"
              />
              <span className="text-sm text-secondary-500">to</span>
              <input
                type="date"
                name="endDate"
                value={damageFilters.endDate}
                onChange={handleDamageFilterChange}
                className="input-field"
              />
            </div>
          </div>
        </div>
        <div className="card-body space-y-4">
          {damagedLoading ? (
            <p className="text-sm text-secondary-500">Loading damaged stock...</p>
          ) : damagedError ? (
            <p className="text-sm text-red-600">{damagedError}</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-secondary-100 bg-white p-4">
                  <p className="text-xs uppercase text-secondary-500">Total Damaged Qty</p>
                  <p className="text-xl font-semibold text-secondary-900">
                    {damagedTotals.totalDamagedQty || 0}
                  </p>
                </div>
                <div className="rounded-lg border border-secondary-100 bg-white p-4">
                  <p className="text-xs uppercase text-secondary-500">Total Damaged Value</p>
                  <p className="text-xl font-semibold text-secondary-900">
                    {formatCurrency(damagedTotals.totalDamagedValue || 0)}
                  </p>
                </div>
              </div>

              {damagedRows.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200">
                    <thead className="bg-secondary-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Product</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Type</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Quantity</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Value</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-secondary-100">
                      {damagedRows.map((row) => (
                        <tr key={row.id}>
                          <td className="px-4 py-2 text-sm text-secondary-900">
                            {new Date(row.date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2 text-sm text-secondary-900">
                            {row.product?.name || 'Unknown'}{row.product?.size ? ` (${row.product.size})` : ''}
                          </td>
                          <td className="px-4 py-2 text-sm text-secondary-700">
                            {row.type === 'arrival' ? 'Arrival' : 'Return'}
                          </td>
                          <td className="px-4 py-2 text-sm text-secondary-900">{row.quantity}</td>
                          <td className="px-4 py-2 text-sm text-secondary-900">
                            {formatCurrency(row.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-secondary-500">No damaged stock in this period.</p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detailed Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card bg-secondary-50">
          <div className="card-header">
            <h3 className="text-lg font-medium text-secondary-900">Sales by Product</h3>
          </div>
          <div className="card-body">
            {salesByProduct.length === 0 ? (
              <p className="text-sm text-secondary-500">No sales data for this month.</p>
            ) : (
              <div className="space-y-4">
                {salesByProduct.map((item) => (
                  <div key={item._id} className="flex justify-between items-center">
                    <span className="text-sm text-secondary-600">
                      {item.product?.name} {item.product?.size ? `- ${item.product.size}` : ''}
                    </span>
                    <span className="text-sm font-medium text-secondary-900">
                      {formatCurrency(item.totalAmount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card bg-secondary-50">
          <div className="card-header">
            <h3 className="text-lg font-medium text-secondary-900">Expenses by Category</h3>
          </div>
          <div className="card-body">
            {expensesByCategory.length === 0 ? (
              <p className="text-sm text-secondary-500">No expense data for this month.</p>
            ) : (
              <div className="space-y-4">
                {expensesByCategory.map((item) => (
                  <div key={item._id} className="flex justify-between items-center">
                    <span className="text-sm text-secondary-600">{item._id}</span>
                    <span className="text-sm font-medium text-secondary-900">
                      {formatCurrency(item.totalAmount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="card bg-secondary-50">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Monthly Performance</h3>
        </div>
        <div className="card-body">
          <p className="text-sm text-secondary-500">
            Charts will appear once more historical data is available.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Reports;
